import React from "react";
import {
  ResponsiveContainer,
  ComposedChart,
  Line,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";

const HumidityCharts = ({ humidityData }) => {
  if (!humidityData || !humidityData.data || humidityData.data.length === 0) {
    return <div>No humidity data available</div>;
  }

  // Extract dew point and wet bulb data if available
  const hasDewPoint =
    humidityData.dewPoint &&
    humidityData.dewPoint.data &&
    humidityData.dewPoint.data.length > 0;
  const hasWetBulb =
    humidityData.wetBulb &&
    humidityData.wetBulb.data &&
    humidityData.wetBulb.data.length > 0;

  // Format the data for the dew point chart
  const formatChartData = (dataArray) => {
    return dataArray.map((item) => {
      // Create month-day label for x-axis
      const monthNames = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
      const monthName = monthNames[item.month - 1];

      return {
        date: `${monthName} ${item.day}`,
        min: item.min,
        max: item.max,
        mean: item.mean,
        dayOfYear: item.day_of_year,
        month: item.month,
        range: item.max - item.min,
      };
    });
  };

  // Get chart data if available
  const dewPointChartData = hasDewPoint
    ? formatChartData(humidityData.dewPoint.data)
    : [];
  const wetBulbChartData = hasWetBulb
    ? formatChartData(humidityData.wetBulb.data)
    : [];

  // Get every month for the X-axis ticks
  const getTickValues = (chartData) => {
    return chartData
      .filter((item) => item.day === 15) // Middle of each month
      .map((item) => item.date.split(" ")[0]); // Only month name
  };

  const dewPointTickValues = hasDewPoint
    ? getTickValues(dewPointChartData)
    : [];
  const wetBulbTickValues = hasWetBulb ? getTickValues(wetBulbChartData) : [];

  // Calculate Y-axis range based on data
  const getAxisRange = (chartData) => {
    const minValue =
      Math.floor(Math.min(...chartData.map((d) => d.min)) / 5) * 5;
    const maxValue =
      Math.ceil(Math.max(...chartData.map((d) => d.max)) / 5) * 5;
    return { minValue, maxValue };
  };

  const dewPointAxisRange = hasDewPoint
    ? getAxisRange(dewPointChartData)
    : { minValue: 0, maxValue: 30 };
  const wetBulbAxisRange = hasWetBulb
    ? getAxisRange(wetBulbChartData)
    : { minValue: 0, maxValue: 30 };

  // Calculate some statistics for interpretation
  const calculateStats = (chartData) => {
    if (!chartData || chartData.length === 0) return null;

    const annualMean =
      chartData.reduce((sum, item) => sum + item.mean, 0) / chartData.length;
    const maxValue = Math.max(...chartData.map((item) => item.max));
    const minValue = Math.min(...chartData.map((item) => item.min));

    // Find month with highest and lowest mean values
    const monthlyMeans = Array(12)
      .fill(0)
      .map((_, i) => {
        const monthData = chartData.filter((item) => item.month === i + 1);
        return {
          month: i + 1,
          mean:
            monthData.reduce((sum, item) => sum + item.mean, 0) /
            monthData.length,
        };
      });

    const highestMonth = monthlyMeans.reduce((prev, current) =>
      prev.mean > current.mean ? prev : current
    );
    const lowestMonth = monthlyMeans.reduce((prev, current) =>
      prev.mean < current.mean ? prev : current
    );

    const monthNames = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];

    return {
      annualMean: annualMean.toFixed(1),
      maxValue: maxValue.toFixed(1),
      minValue: minValue.toFixed(1),
      highestMonth: monthNames[highestMonth.month - 1],
      lowestMonth: monthNames[lowestMonth.month - 1],
      annualRange: (maxValue - minValue).toFixed(1),
    };
  };

  const dewPointStats = hasDewPoint ? calculateStats(dewPointChartData) : null;
  const wetBulbStats = hasWetBulb ? calculateStats(wetBulbChartData) : null;

  return (
    <div className="humidity-charts">
      {hasDewPoint && (
        <div className="chart-section">
          <h3>Dew Point Temperature</h3>
          <div className="chart-container">
            <ResponsiveContainer width="100%" height={400}>
              <ComposedChart
                data={dewPointChartData}
                margin={{ top: 20, right: 30, left: 20, bottom: 30 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="date"
                  ticks={dewPointTickValues}
                  tickFormatter={(value) => value.split(" ")[0]} // Only show month
                  label={{
                    value: "Month",
                    position: "insideBottomRight",
                    offset: -10,
                  }}
                />
                <YAxis
                  domain={[
                    dewPointAxisRange.minValue,
                    dewPointAxisRange.maxValue,
                  ]}
                  label={{
                    value: "°C",
                    angle: -90,
                    position: "insideLeft",
                  }}
                />
                <Tooltip
                  formatter={(value, name) => {
                    return [`${value.toFixed(1)} °C`, name];
                  }}
                  labelFormatter={(label, items) => {
                    const item = items[0]?.payload;
                    return item ? `Day ${item.dayOfYear}: ${label}` : label;
                  }}
                />
                <Legend />

                {/* Dew Point range bar */}
                <Bar
                  dataKey="range"
                  stackId="range"
                  barSize={365}
                  name="Dew Point Temperature Range"
                  fill="#4CAF50"
                  fillOpacity={0.3}
                  isAnimationActive={false}
                  baseValue={(d) => d.min}
                />

                {/* Mean line */}
                <Line
                  type="monotone"
                  dataKey="mean"
                  name="Mean Dew Point Temperature"
                  stroke="#4CAF50"
                  strokeWidth={2}
                  dot={false}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          <div className="chart-info">
            <p>
              <strong>Dew Point Temperature:</strong> The temperature at which
              air becomes saturated with water vapor, causing condensation. It's
              a direct measure of the absolute amount of moisture in the air.
            </p>
            {dewPointStats && (
              <>
                <p>
                  <strong>Data Analysis:</strong> The annual mean dew point for
                  this location is {dewPointStats.annualMean}°C, with a maximum
                  of {dewPointStats.maxValue}°C and a minimum of{" "}
                  {dewPointStats.minValue}°C, giving an annual range of{" "}
                  {dewPointStats.annualRange}°C.
                </p>
                <p>
                  <strong>Seasonal Patterns:</strong> The highest dew point
                  temperatures occur in {dewPointStats.highestMonth}, indicating
                  the most humid period of the year. The lowest dew point
                  temperatures occur in {dewPointStats.lowestMonth},
                  representing the driest air conditions.
                </p>
                <p>
                  <strong>Interpretation:</strong> Dew points below 10°C feel
                  dry and comfortable. Values between 10-15°C feel pleasant,
                  15-20°C feel humid, and above 20°C feel increasingly
                  uncomfortable. Dew points above 24°C are considered
                  oppressive.
                </p>
              </>
            )}
          </div>
        </div>
      )}

      {hasWetBulb && (
        <div className="chart-section">
          <h3>Wet Bulb Temperature</h3>
          <div className="chart-container">
            <ResponsiveContainer width="100%" height={400}>
              <ComposedChart
                data={wetBulbChartData}
                margin={{ top: 20, right: 30, left: 20, bottom: 30 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="date"
                  ticks={wetBulbTickValues}
                  tickFormatter={(value) => value.split(" ")[0]} // Only show month
                  label={{
                    value: "Month",
                    position: "insideBottomRight",
                    offset: -10,
                  }}
                />
                <YAxis
                  domain={[
                    wetBulbAxisRange.minValue,
                    wetBulbAxisRange.maxValue,
                  ]}
                  label={{
                    value: "°C",
                    angle: -90,
                    position: "insideLeft",
                  }}
                />
                <Tooltip
                  formatter={(value, name) => {
                    return [`${value.toFixed(1)} °C`, name];
                  }}
                  labelFormatter={(label, items) => {
                    const item = items[0]?.payload;
                    return item ? `Day ${item.dayOfYear}: ${label}` : label;
                  }}
                />
                <Legend />

                {/* Wet Bulb range bar */}
                <Bar
                  dataKey="range"
                  stackId="range"
                  barSize={365}
                  name="Wet Bulb Temperature Range"
                  fill="#9C27B0"
                  fillOpacity={0.3}
                  isAnimationActive={false}
                  baseValue={(d) => d.min}
                />

                {/* Mean line */}
                <Line
                  type="monotone"
                  dataKey="mean"
                  name="Mean Wet Bulb Temperature"
                  stroke="#9C27B0"
                  strokeWidth={2}
                  dot={false}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          <div className="chart-info">
            <p>
              <strong>Wet Bulb Temperature:</strong> The lowest temperature that
              can be reached by evaporative cooling. It represents the
              temperature felt when skin is wet and exposed to moving air.
            </p>
            {wetBulbStats && (
              <>
                <p>
                  <strong>Data Analysis:</strong> The annual mean wet bulb
                  temperature for this location is {wetBulbStats.annualMean}°C,
                  with a maximum of {wetBulbStats.maxValue}°C and a minimum of{" "}
                  {wetBulbStats.minValue}°C, giving an annual range of{" "}
                  {wetBulbStats.annualRange}°C.
                </p>
                <p>
                  <strong>Seasonal Patterns:</strong> The highest wet bulb
                  temperatures occur in {wetBulbStats.highestMonth}, indicating
                  when evaporative cooling is least effective. The lowest wet
                  bulb temperatures occur in {wetBulbStats.lowestMonth}, when
                  evaporative cooling is most effective.
                </p>
                <p>
                  <strong>Health Implications:</strong> Wet bulb temperatures
                  above 25°C begin to impair physical activity. Values above
                  28°C make sustained work difficult. Wet bulb temperatures of
                  32°C or higher are dangerous as they approach the limit of
                  human survivability, even for healthy individuals at rest.
                </p>
              </>
            )}
          </div>
        </div>
      )}

      {!hasDewPoint && !hasWetBulb && (
        <div className="chart-info">
          <p>
            Dew point and wet bulb temperature data are not available for this
            location.
          </p>
        </div>
      )}
    </div>
  );
};

export default HumidityCharts;
